import { faCheck, faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, CircularProgress, ListItemText, Menu, MenuItem } from '@mui/material';
import React, { ReactNode, useMemo, useState, useEffect } from 'react';

import { SearchInput } from './search-input';
import { filterByTime, TimeFilters } from './time-filters';
import './styles.scss';

export interface IListItemVols {
  tenor: string;
  tag: string;
  value: number;
}

export interface IListItemValues {
  key: string;
  label: string;
  timestamp?: string;
}

interface IDropsownFilteredListProps {
  values: IListItemValues[];
  onChange: (value: IListItemValues) => void;
  className?: string;
  label?: string;
  renderLabelSelected?: ReactNode;
  searchFieldsFiltered?: any[];
  timeFilters?: string[];
  isShowTimeFilters?: boolean;
  isShowSearchInput?: boolean;
  customLabel?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: IListItemValues;
  isButtonIcon?: boolean;
  isDropdownLoadingData?: boolean;
}

interface IFilterOptions<T> {
  values: T[];
  searchTerm: string;
  searchFields?: (keyof T)[];
  selectedTime: string;
  timeFilter?: (items: T[], selectedTime: string) => T[];
}

export const DropdownFilteredList = ({
  values,
  onChange,
  className,
  label,
  renderLabelSelected,
  searchFieldsFiltered,
  timeFilters,
  disabled = false,
  defaultValue,
  isButtonIcon = false,
  isShowSearchInput = false,
  isShowTimeFilters = false,
  isDropdownLoadingData = false,
}: IDropsownFilteredListProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selected, setSelected] = useState<any | null>(defaultValue || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTime, setSelectedTime] = useState('All');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (item: any) => {
    setSelected(item);
    onChange(item);
    handleClose();
  };

  useEffect(() => {
    if (defaultValue) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  function filterItems<T>({
    values,
    searchTerm,
    searchFields = ['label'] as (keyof T)[],
    selectedTime,
    timeFilter = filterByTime,
  }: IFilterOptions<T>): T[] {
    const lowerSearchTerm = searchTerm.toLowerCase();

    const filteredBySearch = values?.filter((item) =>
      searchFields.some((field) => {
        const fieldValue = item[field];
        return fieldValue ? String(fieldValue).toLowerCase().includes(lowerSearchTerm) : false;
      }),
    );

    return timeFilter(filteredBySearch, selectedTime);
  }

  const filteredValues = useMemo(() => {
    return filterItems({
      values,
      searchTerm,
      selectedTime,
      searchFields: searchFieldsFiltered,
    });
  }, [values, searchTerm, selectedTime]);

  const renderItem = (item: any, level: number = 0) => {
    return (
      <MenuItem
        key={item.key}
        selected={selected?.key === item.key}
        onClick={() => handleChange(item)}
        sx={{
          paddingLeft: `${(level + 1) * 16}px`,
          paddingY: '10px',
        }}
      >
        <ListItemText>
          <div className="item-details">
            <div className="item-header">
              <span className="item-timestamp">{item.label}</span>
              {selected?.key === item.key && (
                <span className="selected-check-icon">
                  <FontAwesomeIcon icon={faCheck} className="icon" />
                </span>
              )}
            </div>
          </div>
        </ListItemText>
      </MenuItem>
    );
  };

  return (
    <div className={`dropdown-filtered-list-container`}>
      {label && <span className="label">{label}</span>}
      <Button
        onClick={handleClick}
        aria-controls={open ? 'menu' : undefined}
        aria-haspopup="true"
        disableRipple
        aria-expanded={open ? 'true' : undefined}
        variant="text"
        className={open ? 'selector-button selector-button-open' : 'selector-button'}
        endIcon={
          isButtonIcon &&
          (open ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />)
        }
      >
        {renderLabelSelected}
      </Button>

      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className={`filtered-select-menu ${className || ''}`}
      >
        <span className="dropdown-filtered-list-title">Filtered by</span>
        {isShowSearchInput && <SearchInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} />}
        {isShowTimeFilters && (
          <TimeFilters
            selectedTime={selectedTime}
            setSelectedTime={setSelectedTime}
            timeFilters={timeFilters}
          />
        )}

        {!isDropdownLoadingData ? (
          <div className="filtered-select-menu-list-content">
            <div className="filtered-select-menu-list">
              {filteredValues.map((item) => renderItem(item))}
            </div>
            {filteredValues.length === 0 && (
              <div className="filtered-not-results">No results found</div>
            )}
          </div>
        ) : (
          <div className="dropdown-loading-data">
            <CircularProgress />
          </div>
        )}
      </Menu>
    </div>
  );
};
